import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  InspectionListItem,
  InspectionStats,
  InspectionStatsCriteria,
  InspectionTab,
} from '@hetivo/hetivo-lib-types';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { useMasterContext } from '&/master';

import type { SearchInspectionsParams } from '../api';
import type { InspectionsListSearch } from '../lib/inspections-list-search';
import { TAB_STATUS, toInspectionTab } from '../lib/inspections-list-search';
import { useInspectionStats } from './use-inspection-stats';
import { useInspectionsSearch } from './use-inspections-search';

// ─── Types ────────────────────────────────────────────────────────────────────

/** The list's URL search state + a merge-setter, bound to the route by {@link useInspectionsListSearchState}. */
export interface InspectionsListSearchState {
  search: InspectionsListSearch;
  setSearch: (patch: Partial<InspectionsListSearch>) => void;
}

export interface UseInspectionsPageReturn {
  activeTab: InspectionTab;
  currentPage: number;
  /** The list request failed: the page shows the load-error alert instead of the table. */
  hasLoadError: boolean;
  inspections: InspectionListItem[];
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  onTabChange: (tab: InspectionTab) => void;
  pageSize: number;
  /** The tab counts; `undefined` while loading and whenever a request failed, so no tab reads a false 0. */
  stats: InspectionStats | undefined;
  totalItems: number;
  totalPages: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

// ─── Scope ────────────────────────────────────────────────────────────────────

/**
 * The master selector's narrowing, as the stats and search criteria: a key is sent only when one is selected,
 * never `null`. The page and the sidebar badge both build their criteria here, so they hash to one stats key.
 */
export function useInspectionScope(): InspectionStatsCriteria {
  const { siteId, territoryId } = useMasterContext();

  return useMemo(
    (): InspectionStatsCriteria => ({ locationId: siteId ?? undefined, territoryId: territoryId ?? undefined }),
    [siteId, territoryId],
  );
}

// ─── Route glue ───────────────────────────────────────────────────────────────

/** Binds the list state to the route's URL search params. Intra-list changes use `replace: true`. */
export function useInspectionsListSearchState(): InspectionsListSearchState {
  const search = useSearch({ from: '/_authenticated/inspections' });
  const navigate = useNavigate();
  const setSearch = useCallback(
    (patch: Partial<InspectionsListSearch>): void => {
      void navigate({
        replace: true,
        search: (prev: InspectionsListSearch): InspectionsListSearch => ({ ...prev, ...patch }),
        to: '/inspections',
      });
    },
    [navigate],
  );
  return { search, setSearch };
}

// ─── Page reset ───────────────────────────────────────────────────────────────

/**
 * The page to list: a new master selector scope resets it to 1. The page lives in the URL, so the reset is a
 * navigation; until it lands this returns 1 (not the stale page, which may not exist in the new scope, and
 * must not be fetched), and the scope is only marked settled once the URL page is 1.
 */
function useScopeResetPage(
  scope: InspectionStatsCriteria,
  urlPage: number,
  setSearch: InspectionsListSearchState['setSearch'],
): number {
  const [pageScope, setPageScope] = useState(scope);
  const scopeChanged = pageScope !== scope;
  useEffect(() => {
    if (!scopeChanged) {
      return;
    }
    if (urlPage === 1) {
      setPageScope(scope);
    } else {
      setSearch({ page: 1 });
    }
  }, [scope, scopeChanged, setSearch, urlPage]);
  return scopeChanged ? 1 : urlPage;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Derives the Inspections list view from the URL search state and the master selector (SWOOP-400): the active
 * tab (a status), the page, the rows and the three tab counts. A tab change or a master selector change resets
 * to page 1. The counts come from the same stats endpoint and key as the sidebar badge.
 */
export function useInspectionsPage({ search, setSearch }: InspectionsListSearchState): UseInspectionsPageReturn {
  const scope = useInspectionScope();
  const activeTab = toInspectionTab(search.tab);

  const currentPage = useScopeResetPage(scope, search.page ?? 1, setSearch);

  const searchParams = useMemo(
    (): SearchInspectionsParams => ({
      criteria: { ...scope, status: TAB_STATUS[activeTab] },
      limit: PAGE_SIZE,
      page: currentPage,
    }),
    [activeTab, currentPage, scope],
  );

  const list = useInspectionsSearch(searchParams);
  const counts = useInspectionStats(scope);

  const onTabChange = useCallback(
    (tab: InspectionTab): void => {
      setSearch({ page: 1, tab });
    },
    [setSearch],
  );
  const onPageChange = useCallback(
    (page: number): void => {
      setSearch({ page });
    },
    [setSearch],
  );
  const { refetch: refetchList } = list;
  const { isError: statsFailed, refetch: refetchStats } = counts;
  const onRetry = useCallback((): void => {
    void refetchList();
    if (statsFailed) {
      void refetchStats();
    }
  }, [refetchList, refetchStats, statsFailed]);

  return {
    activeTab,
    currentPage,
    hasLoadError: list.isError,
    inspections: list.data?.items ?? [],
    isLoading: list.isLoading,
    onPageChange,
    onRetry,
    onTabChange,
    pageSize: PAGE_SIZE,
    stats: list.isError || statsFailed ? undefined : counts.data,
    totalItems: list.data?.total ?? 0,
    totalPages: list.data?.totalPages ?? 1,
  };
}
